import {
    addDoc,
    collection,
    doc,
    DocumentReference,
    getDoc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    where,
} from 'firebase/firestore';
import { db } from '@/firebase';

export type Book = {
    id: string;
    shikakuRef: DocumentReference;
    title: string;
    edition?: string;
    isPublished: boolean;
    createdAt?: unknown;
    updatedAt?: unknown;
};

export type Chapter = {
    id: string;
    bookRef: DocumentReference;
    title: string;
    order: number;
    breakBefore?: boolean;
    partLabel?: string;
    partOrder?: number;
};

export type Section = {
    id: string;
    bookRef: DocumentReference;
    chapterRef: DocumentReference;
    title: string;
    order: number;
    sourcePdfPages?: number[];
};

export type BlockType = 'paragraph' | 'image' | 'table' | 'list' | 'note' | 'subheading' | 'scale';

export type ScaleTone = 'neutral' | 'warning' | 'danger';

export type ScaleStep = {
    value: string;
    label: string;
    tone: ScaleTone;
};

export type Block = {
    id: string;
    bookRef: DocumentReference;
    chapterRef: DocumentReference;
    sectionRef: DocumentReference;
    type: BlockType;
    order: number;
    content: Record<string, unknown>;
    options?: {
        keepWithNext?: boolean;
        avoidPageBreak?: boolean;
    };
};

export const DEFAULT_ORDER_STEP = 1000;

export const createBook = async (payload: {
    shikakuId: string;
    title: string;
    edition?: string;
    isPublished?: boolean;
}) => {
    const shikakuRef = doc(db, 'Shikaku', payload.shikakuId);
    const bookDoc = await addDoc(collection(db, 'books'), {
        shikakuRef,
        title: payload.title,
        edition: payload.edition ?? '1.0',
        isPublished: payload.isPublished ?? false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return bookDoc;
};

export const createChapter = async (payload: {
    bookRef: DocumentReference;
    title: string;
    order: number;
    breakBefore?: boolean;
}) =>
    addDoc(collection(db, 'chapters'), {
        bookRef: payload.bookRef,
        title: payload.title,
        order: payload.order,
        breakBefore: payload.breakBefore ?? false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });

export const createSection = async (payload: {
    bookRef: DocumentReference;
    chapterRef: DocumentReference;
    title: string;
    order: number;
}) =>
    addDoc(collection(db, 'sections'), {
        bookRef: payload.bookRef,
        chapterRef: payload.chapterRef,
        title: payload.title,
        order: payload.order,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });

export const createBlock = async (payload: {
    bookRef: DocumentReference;
    chapterRef: DocumentReference;
    sectionRef: DocumentReference;
    type: BlockType;
    order: number;
    content: Record<string, unknown>;
    options?: Block['options'];
}) =>
    addDoc(collection(db, 'blocks'), {
        bookRef: payload.bookRef,
        chapterRef: payload.chapterRef,
        sectionRef: payload.sectionRef,
        type: payload.type,
        order: payload.order,
        content: payload.content,
        options: payload.options ?? {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });

export const getBook = async (bookId: string) => {
    const snapshot = await getDoc(doc(db, 'books', bookId));
    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...snapshot.data() } as Book;
};

export const listAllBooks = async () => {
    const snapshot = await getDocs(collection(db, 'books'));
    return snapshot.docs.map((bookDoc) => ({ id: bookDoc.id, ...bookDoc.data() })) as Book[];
};

export const listBooksForShikaku = async (shikakuId: string) => {
    const shikakuRef = doc(db, 'Shikaku', shikakuId);
    const snapshot = await getDocs(query(collection(db, 'books'), where('shikakuRef', '==', shikakuRef)));
    return snapshot.docs.map((bookDoc) => ({ id: bookDoc.id, ...bookDoc.data() })) as Book[];
};

export const listChaptersForBook = async (bookRef: DocumentReference) => {
    const snapshot = await getDocs(query(collection(db, 'chapters'), where('bookRef', '==', bookRef), orderBy('order')));
    return snapshot.docs.map((chapterDoc) => ({ id: chapterDoc.id, ...chapterDoc.data() })) as Chapter[];
};

export const listSectionsForBook = async (bookRef: DocumentReference) => {
    const snapshot = await getDocs(query(collection(db, 'sections'), where('bookRef', '==', bookRef), orderBy('order')));
    return snapshot.docs.map((sectionDoc) => ({ id: sectionDoc.id, ...sectionDoc.data() })) as Section[];
};

export const listBlocksForBook = async (bookRef: DocumentReference) => {
    const snapshot = await getDocs(query(collection(db, 'blocks'), where('bookRef', '==', bookRef), orderBy('order')));
    return snapshot.docs.map((blockDoc) => ({ id: blockDoc.id, ...blockDoc.data() })) as Block[];
};
